import React, { FormEvent, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ContainerBase } from 'src/components/widgets/layouts';
import Stack from 'src/components/widgets/stack/stack';
import { TextArea } from 'src/components/widgets/textArea';
import { T, backofficeUrl, stringFormat, useQueryParams } from 'src/shared/helpers';
import ArrowBackIcon from 'src/shared/material_icons/navigation/arrow_back.svg';
import CloseIcon from 'src/shared/material_icons/navigation/close.svg';
import LogoZakeke from 'src/shared/svg/logoZakeke';
import styled, { css } from 'styled-components';
import { BasicButton, PrimaryButton } from '../../widgets/buttons';
import { Icon } from '../../widgets/icon';
import OnboardingSolutions from './onboardingSolutions';
import { useSuggestTool } from './useSuggestTool';
import { useOnboardingEventTracking } from 'src/shared/hooks/useOnboardingEventTracking';
import { showErrorNotification } from '../../widgets/notificationToaster';
import { ONBOARDING_TOOLS, OnboardingTool, PROMPT_PLACEHOLDER, PROMPT_PRESETS } from './toolSuggestion';

// The Zakeke palette — orange #F08035, green #98C326, fuchsia #E61780 — as soft washes on an
// off-white ground, so the page is recognisably ours without fighting the prompt for attention.
const Container = styled(ContainerBase)`
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    /* Fullscreen routes have no outer gutter, so the background can own the viewport height directly
       and still grow naturally when the solutions grid is taller than the screen. */
    flex: 1;
    min-height: 100vh;
    /* Room at the top for the logo and at the bottom for the shortcut row, both out of the flow. */
    padding: 72px 20px 80px;
    box-sizing: border-box;
    background:
        radial-gradient(circle at 12% 14%, rgba(240, 128, 53, 0.1), transparent 50%),
        radial-gradient(circle at 88% 12%, rgba(152, 195, 38, 0.09), transparent 48%),
        radial-gradient(circle at 78% 86%, rgba(230, 23, 128, 0.07), transparent 50%),
        radial-gradient(circle at 18% 92%, rgba(240, 128, 53, 0.05), transparent 46%), #fcfcfb;
`;

// Top bar of the page: the mark on the left; on the right the step indicator, followed by the way back
// or out when there is one.
const TopBar = styled.div`
    position: absolute;
    top: 28px;
    left: 32px;
    right: 32px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
`;

const TopBarEnd = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;
`;

const StepIndicator = styled.span`
    font-size: 13px;
    color: #555;
    white-space: nowrap;
`;

const ONBOARDING_STEP_COUNT = 2;

const BrandLogo = styled(LogoZakeke)`
    height: 20px;
    width: auto;
`;

// The suggestion is wider than the form, to leave room for the preview beside the details.
const Content = styled.div<{ $wide: boolean }>`
    width: 100%;
    max-width: ${(props) => (props.$wide ? '880px' : '640px')};
`;

const ACCENT = '#f08035';
const INK = '#263238';
const MUTE = '#607d8b';
const LINE = '#e3e7ec';
const FOCUS = '#0054c4';

const Title = styled.h1`
    font-size: 32px;
    font-weight: 600;
    line-height: 1.18;
    letter-spacing: -0.015em;
    text-align: center;
    margin: 0;
    color: ${(props) => props.theme.app.component.pageTitle.textColor};

    @media (max-width: 640px) {
        font-size: 26px;
    }
`;

const Subtitle = styled.p`
    max-width: 52ch;
    margin: 0 auto;
    font-size: 15.5px;
    text-align: center;
    color: #555;

    @media (max-width: 640px) {
        font-size: 14.5px;
    }
`;

const PromptArea = styled(TextArea)`
    min-height: 84px;
    padding: 11px 12px;
    font-size: 15px;
    line-height: 1.55;
`;

// Quiet, below everything: the way out for the seller who would rather read what every solution does.
const ExploreFoot = styled.p`
    margin: 0;
    text-align: center;
    font-size: 14px;
`;

// Says what is happening while the pick or the description is being routed; read out by screen readers.
const StatusNote = styled.p`
    min-height: 19px;
    margin: 0;
    text-align: center;
    font-size: 13px;
    color: ${MUTE};
`;

// The goal card and the solution card below it share their side padding and the look of their small
// uppercase label, so the two read as one column: only the label colour tells them apart.
const SUGGESTION_SIDE_PADDING = '32px';
const SUGGESTION_SIDE_PADDING_NARROW = '24px';

const suggestionLabel = css`
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.1em;
`;

// A slim card above the suggestion recalling what the seller picked or wrote, with the way back to change it.
const GoalCard = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    background: #fff;
    border: 1px solid ${LINE};
    border-radius: 14px;
    padding: 16px ${SUGGESTION_SIDE_PADDING};

    @media (max-width: 560px) {
        padding: 16px ${SUGGESTION_SIDE_PADDING_NARROW};
    }
`;

// The label sits above the answer; together they take the room left by the Change link.
const GoalContent = styled.div`
    flex: 1;
    min-width: 0;
`;

const GoalLabel = styled.p`
    ${suggestionLabel};
    margin: 0 0 4px 0;
    color: ${MUTE};
`;

const GoalText = styled.p`
    margin: 0;
    font-size: 15px;
    line-height: 1.5;
    color: ${INK};
    overflow-wrap: anywhere;
`;

const TextLink = styled.button`
    flex-shrink: 0;
    padding: 0;
    border: none;
    background: none;
    font-family: ${(props) => props.theme.app.font};
    font-size: inherit;
    color: #555;
    text-decoration: underline;
    cursor: pointer;

    &:hover {
        color: #1a1a1a;
    }
`;

const ExploreLink = styled(TextLink)`
    color: ${MUTE};
    text-underline-offset: 3px;

    &:hover {
        color: ${INK};
    }

    &:focus-visible {
        outline: 3px solid ${FOCUS};
        outline-offset: 2px;
        border-radius: 4px;
    }
`;

const GoalChange = styled(TextLink)`
    font-size: 13px;
`;

// A reassurance right under the call to action: the account is not empty to start with.
const DemoNote = styled.p`
    margin: 10px 0 0;
    font-size: 12px;
    color: #aaa;
`;

const AlternativesNote = styled.p`
    margin: 32px 0 0;
    font-size: 12px;
    color: #888;
`;

// The details on the left; the preview of the solution on the right, flush with the card edges.
const ToolCard = styled.div`
    display: grid;
    grid-template-columns: minmax(0, 3fr) minmax(0, 2fr);
    overflow: hidden;
    background: #fff;
    border: 1px solid ${LINE};
    border-radius: 14px;

    @media (max-width: 560px) {
        grid-template-columns: 1fr;
    }
`;

const ToolPreview = styled.div`
    min-height: 200px;
    background: #f4f6f7;
`;

const ToolPreviewVideo = styled.video`
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
`;

const ToolDetails = styled.div`
    padding: 32px ${SUGGESTION_SIDE_PADDING};

    @media (max-width: 560px) {
        padding: 24px ${SUGGESTION_SIDE_PADDING_NARROW};
    }
`;

const CardLabel = styled.p`
    ${suggestionLabel};
    color: ${ACCENT};
    margin: 0 0 14px 0;
`;

// The product name is a label, as on the solutions view: the goal below it is the headline.
const ToolName = styled.p`
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 0 0 8px 0;
    font-size: 13px;
    font-weight: 600;
    color: ${MUTE};

    svg {
        flex-shrink: 0;
        width: 22px;
        height: 22px;
    }
`;

const ToolGoal = styled.h2`
    margin: 0 0 20px 0;
    font-size: 26px;
    font-weight: 600;
    line-height: 1.22;
    letter-spacing: -0.01em;
    color: ${INK};
`;

// The lead of each feature is set in bold, so the list reads at a glance.
const FeatureList = styled.ul`
    margin: 0 0 28px 0;
    padding-left: 18px;
    font-size: 14px;
    line-height: 1.6;
    color: #555;

    li + li {
        margin-top: 8px;
    }

    li::marker {
        color: ${ACCENT};
    }

    strong {
        font-weight: 600;
        color: ${INK};
    }
`;

// The one action of the suggestion, a step above the standard large button.
const ToolCta = styled(PrimaryButton)`
    && {
        height: 46px;
        min-height: 46px;
        padding: 0 24px;
        font-size: 14.5px;
        font-weight: 600;
    }
`;

// Sets in bold the part of a feature written between ** markers.
const renderFeature = (feature: string) => feature.split('**').map((part, index) => (index % 2 === 1 ? <strong key={index}>{part}</strong> : part));

// The ready-made problems are a list the seller scans top to bottom, rather than a set of chips: one of
// them is usually close enough, and reading them is the whole point. Picking one is the answer, so it
// runs the suggestion straight away.
const OptionList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

const OptionArrow = styled.i`
    font-style: normal;
    font-size: 17px;
    color: ${ACCENT};
    opacity: 0;
    transition:
        opacity 0.12s ease,
        transform 0.12s ease;
`;

const OptionText = styled.span`
    flex: 1;
`;

const OptionRow = styled.button`
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    box-sizing: border-box;
    text-align: left;
    background: #fff;
    border: 1px solid ${LINE};
    border-radius: 12px;
    padding: 16px 18px;
    font-family: ${(props) => props.theme.app.font};
    font-size: 15px;
    line-height: 1.4;
    color: ${INK};
    cursor: pointer;
    transition:
        border-color 0.12s ease,
        background 0.12s ease;

    &:hover:not(:disabled) {
        border-color: ${ACCENT};
        background: #fff8f4;
    }

    &:hover:not(:disabled),
    &:focus-visible {
        ${OptionArrow} {
            opacity: 1;
            transform: translateX(3px);
        }
    }

    &:focus-visible {
        outline: 3px solid ${FOCUS};
        outline-offset: 2px;
    }

    &:disabled {
        cursor: default;
        opacity: 0.45;
    }

    @media (max-width: 640px) {
        padding: 14px 16px;
        font-size: 14.5px;
    }

    @media (prefers-reduced-motion: reduce) {
        transition: none;

        ${OptionArrow} {
            transition: none;
        }
    }
`;

// The way out of the list: dashed and unfilled while closed, a white card holding the box once open.
const FreeText = styled.div<{ $isOpen: boolean }>`
    margin-top: 4px;
    border: 1px ${(props) => (props.$isOpen ? `solid ${LINE}` : 'dashed #cbd3da')};
    border-radius: 12px;
    background: ${(props) => (props.$isOpen ? '#fff' : 'transparent')};
    transition: background 0.12s ease;
`;

const FreeTextToggle = styled.button`
    display: block;
    width: 100%;
    box-sizing: border-box;
    padding: 16px 18px;
    border: none;
    background: none;
    text-align: left;
    font-family: ${(props) => props.theme.app.font};
    font-size: 15px;
    color: #555;
    cursor: pointer;

    &:hover {
        color: ${INK};
    }

    &:focus-visible {
        outline: 3px solid ${FOCUS};
        outline-offset: -3px;
        border-radius: 12px;
    }

    &:disabled {
        cursor: default;
        opacity: 0.45;
    }
`;

const FreeTextPanel = styled.form`
    padding: 16px 18px;
`;

const FreeTextLabel = styled.label`
    display: block;
    margin-bottom: 6px;
    font-size: 13px;
    color: ${MUTE};
`;

const FreeTextError = styled.p`
    margin: 7px 0 0;
    font-size: 13px;
    color: #c62828;
`;

const FreeTextActions = styled.div`
    display: flex;
    gap: 9px;
    margin-top: 11px;
`;

// Deliberately quiet, and out of the flow at the very bottom so the list stays centred on the screen.
// It belongs to the onboarding a seller meets after signup: someone who reopened this page from the
// solution switcher is already inside a tool and has the switcher itself for jumping around.
const ShortcutFooter = styled.div`
    position: absolute;
    left: 20px;
    right: 20px;
    bottom: 28px;
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    align-items: baseline;
    gap: 4px 14px;
    font-size: 12px;
    color: #aaa;
`;

const Shortcut = styled.button`
    background: none;
    border: none;
    padding: 0;
    font-family: ${(props) => props.theme.app.font};
    font-size: 12px;
    color: #999;
    text-decoration: underline;
    cursor: pointer;

    &:hover {
        color: #555;
    }
`;

type Step = 'prompt' | 'solutions' | 'describe' | 'suggestion';

type OnboardingAnswer = { prompt: string; isCustom: boolean };

export const ONBOARDING_SOLUTIONS_PATH = '/onboarding/solutions';
export const ONBOARDING_ASK_ZAKEKE_PATH = '/onboarding/ask-zakeke';
export const ONBOARDING_RESULT_PATH = '/onboarding/result';

// Marks the page as reopened from inside the app rather than met right after signup. It travels in the
// URL rather than in the router state so it survives a reload and a pasted link.
export const ONBOARDING_FROM_APP_QUERY = 'from=app';

const Onboarding = () => {
    const navigate = useNavigate();
    const suggestTool = useSuggestTool();
    const trackEvent = useOnboardingEventTracking();
    const location = useLocation();
    const querystringParams = useQueryParams();
    const promptRef = useRef<HTMLTextAreaElement>(null);
    const trackedPageKey = useRef<string | null>(null);

    // Set by the solution switcher: the seller was already working inside a tool, so the page offers a
    // way out and drops the shortcuts. Right after signup there is nothing to go back to.
    const isOpenedFromApp = querystringParams.get('from') === 'app';
    const entryPoint = isOpenedFromApp ? 'navigation' : 'signup';
    // The path identifies the screen; the query only carries the selected answer and entry context.
    const goal = querystringParams.get('goal');
    const isCustom = querystringParams.get('custom') === '1';
    const toolId = querystringParams.get('tool');
    const suggestion = goal && toolId ? { prompt: goal, isCustom, toolId } : null;
    const suggestedTool = suggestion ? (ONBOARDING_TOOLS.find((tool) => tool.id === suggestion.toolId) ?? null) : null;
    const step: Step = location.pathname.endsWith(ONBOARDING_RESULT_PATH)
        ? 'suggestion'
        : location.pathname.endsWith(ONBOARDING_ASK_ZAKEKE_PATH)
          ? 'describe'
          : location.pathname.endsWith(ONBOARDING_SOLUTIONS_PATH)
            ? 'solutions'
            : 'prompt';
    const onboardingUrl = (path: string, params: URLSearchParams) => {
        const query = params.toString();
        return `${backofficeUrl(path)}${query ? `?${query}` : ''}`;
    };
    const promptUrl = (draft?: OnboardingAnswer) => {
        const params = new URLSearchParams();
        if (isOpenedFromApp) params.set('from', 'app');
        if (draft?.isCustom) params.set('draft', draft.prompt);
        return onboardingUrl('/onboarding', params);
    };

    useEffect(() => {
        if (trackedPageKey.current === location.key) return;
        trackedPageKey.current = location.key;
        if (step === 'suggestion') {
            if (!suggestedTool || !suggestion) {
                navigate(promptUrl(), { replace: true });
                return;
            }
            const trackResult = async () => {
                if (location.state?.trackAiSuggestionReceived) {
                    await trackEvent({
                        eventName: 'ai_suggestion_received',
                        entryPoint,
                        selectionMode: 'ai',
                        targetSolution: suggestion.toolId,
                        promptText: suggestion.prompt
                    });
                }
                await trackEvent({
                    eventName: 'solution_result_opened',
                    entryPoint,
                    selectionMode: suggestion.isCustom ? 'ai' : 'fixed',
                    targetSolution: suggestion.toolId,
                    promptText: suggestion.prompt,
                    context: { path: location.pathname }
                });
            };
            void trackResult();
            return;
        }
        const eventName = {
            prompt: 'onboarding_sentences_opened',
            solutions: 'solutions_catalog_opened',
            describe: 'ask_zakeke_which_fits_opened'
        }[step];
        trackEvent({ eventName, entryPoint, context: { path: location.pathname } });
    }, [step, entryPoint, location.pathname, location.key, location.state, goal, toolId, isCustom, suggestedTool, trackEvent, navigate, isOpenedFromApp]);

    // A written answer reopens its box with the text in it when the seller comes back; a picked one leaves
    // the list as it was, since picking again is a single click.
    const [isFreeTextOpen, setIsFreeTextOpen] = useState(!!querystringParams.get('draft'));
    const [freeText, setFreeText] = useState(querystringParams.get('draft') ?? '');
    const [isFreeTextEmptyError, setIsFreeTextEmptyError] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (step !== 'prompt') return;
        const draft = new URLSearchParams(location.search).get('draft') ?? '';
        setIsFreeTextOpen(!!draft);
        setFreeText(draft);
    }, [step, location.search]);

    const goBackFromSolutions = () => {
        if (step === 'describe') {
            const params = new URLSearchParams();
            if (isOpenedFromApp) params.set('from', 'app');
            if (querystringParams.get('draft')) params.set('draft', querystringParams.get('draft')!);
            if (suggestion) {
                params.set('goal', suggestion.prompt);
                params.set('tool', suggestion.toolId);
                if (suggestion.isCustom) params.set('custom', '1');
            }
            navigate(onboardingUrl(ONBOARDING_SOLUTIONS_PATH, params));
        } else if (suggestion) {
            navigate(onboardingUrl(ONBOARDING_RESULT_PATH, new URLSearchParams(location.search)));
        } else navigate(promptUrl(querystringParams.get('draft') ? { prompt: querystringParams.get('draft')!, isCustom: true } : undefined));
    };
    const goToSolutions = () => {
        const params = new URLSearchParams();
        if (isOpenedFromApp) params.set('from', 'app');
        if (step === 'suggestion' && suggestion) {
            params.set('goal', suggestion.prompt);
            params.set('tool', suggestion.toolId);
            if (suggestion.isCustom) params.set('custom', '1');
        }
        if (step === 'prompt' && isFreeTextOpen && freeText) params.set('draft', freeText);
        navigate(onboardingUrl(ONBOARDING_SOLUTIONS_PATH, params));
    };
    const setSolutionsView = (describe: boolean) => {
        const params = new URLSearchParams();
        if (isOpenedFromApp) params.set('from', 'app');
        if (querystringParams.get('draft')) params.set('draft', querystringParams.get('draft')!);
        if (suggestion) {
            params.set('goal', suggestion.prompt);
            params.set('tool', suggestion.toolId);
            if (suggestion.isCustom) params.set('custom', '1');
        }
        navigate(onboardingUrl(describe ? ONBOARDING_ASK_ZAKEKE_PATH : ONBOARDING_SOLUTIONS_PATH, params));
    };

    const goToTool = async (tool: OnboardingTool, source: 'solution_result' | 'shortcut' | 'solutions_catalog') => {
        const tracking = trackEvent({
            eventName: 'solution_selected',
            entryPoint,
            selectionMode: source === 'solutions_catalog' ? 'navigation' : source === 'solution_result' && suggestion?.isCustom ? 'ai' : 'fixed',
            targetSolution: tool.id,
            promptText: source === 'solution_result' ? suggestion?.prompt : undefined,
            context: { source }
        });
        await tracking.catch(() => undefined);
        window.location.href = tool.url;
    };

    const changeGoal = () => {
        if (!suggestion) return;
        trackEvent({
            eventName: 'goal_change_clicked',
            entryPoint,
            selectionMode: suggestion.isCustom ? 'ai' : 'fixed',
            targetSolution: suggestion.toolId,
            promptText: suggestion.prompt,
            context: { source: 'solution_result', isCustom: suggestion.isCustom }
        });
        navigate(promptUrl(suggestion));
    };

    const showSuggestion = (draft: OnboardingAnswer, toolId: OnboardingTool['id']) => {
        if (step === 'prompt' && draft.isCustom) navigate(promptUrl(draft), { replace: true });
        const params = new URLSearchParams();
        if (isOpenedFromApp) params.set('from', 'app');
        params.set('tool', toolId);
        params.set('goal', draft.prompt);
        if (draft.isCustom) params.set('custom', '1');
        navigate(onboardingUrl(ONBOARDING_RESULT_PATH, params), { state: { trackAiSuggestionReceived: draft.isCustom } });
    };

    // Free text, from the form or the solutions description box, still needs an AI recommendation.
    const runSuggestion = async (text: string, isCustomAnswer: boolean) => {
        if (!text.trim() || isLoading) return;

        setIsLoading(true);
        try {
            await trackEvent({ eventName: 'ai_prompt_submitted', entryPoint, selectionMode: 'ai', promptText: text, context: { isCustom: isCustomAnswer } });
            const { tool } = await suggestTool(text);
            showSuggestion({ prompt: text, isCustom: isCustomAnswer }, tool.id);
        } catch {
            showErrorNotification({ message: T._('Unable to suggest a solution. Please try again.', 'Admin') });
        } finally {
            setIsLoading(false);
        }
    };

    const onOptionClick = (preset: (typeof PROMPT_PRESETS)[number]) => {
        if (isLoading) return;

        trackEvent({
            eventName: 'fixed_option_selected',
            entryPoint,
            selectionMode: 'fixed',
            targetSolution: preset.toolId,
            context: { source: 'sentences_list', label: preset.label }
        });
        showSuggestion({ prompt: preset.label, isCustom: false }, preset.toolId);
    };

    const onFreeTextOpen = () => {
        setIsFreeTextOpen(true);
        window.setTimeout(() => promptRef.current?.focus(), 0);
    };

    const onFreeTextCancel = () => {
        setIsFreeTextOpen(false);
        setIsFreeTextEmptyError(false);
    };

    // An empty box is not sent: the seller is asked for a line first.
    const onFreeTextSubmit = (event: FormEvent) => {
        event.preventDefault();

        if (!freeText.trim()) {
            setIsFreeTextEmptyError(true);
            promptRef.current?.focus();
            return;
        }

        runSuggestion(freeText, true);
    };

    return (
        <Container>
            <TopBar>
                <BrandLogo aria-label='Zakeke' />
                <TopBarEnd>
                    {/* The catalog and Ask Zakeke are side paths, not steps in the two-step prompt flow. */}
                    {step !== 'solutions' && step !== 'describe' && (
                        <StepIndicator>
                            {stringFormat(T._('Step {0} of {1}', 'Admin'), step === 'prompt' ? '1' : '2', `${ONBOARDING_STEP_COUNT}`)}
                            {` · ${step === 'prompt' ? T._('about 30 seconds', 'Admin') : T._('last one', 'Admin')}`}
                        </StepIndicator>
                    )}
                    {/* Opened from the app, every screen closes back to the backoffice; right after signup the
                        solutions view only leads back to the form. */}
                    {isOpenedFromApp ? (
                        <BasicButton
                            type='button'
                            $size='small'
                            $type='icon'
                            aria-label={T._('Close', 'Admin')}
                            onClick={() => navigate(backofficeUrl('/dashboard'), { replace: true })}
                        >
                            <Icon>
                                <CloseIcon />
                            </Icon>
                        </BasicButton>
                    ) : (
                        (step === 'solutions' || step === 'describe') && (
                            <BasicButton type='button' $size='small' onClick={goBackFromSolutions}>
                                <Icon>
                                    <ArrowBackIcon />
                                </Icon>
                                {T._('Back', 'Admin')}
                            </BasicButton>
                        )
                    )}
                </TopBarEnd>
            </TopBar>

            {step === 'solutions' || step === 'describe' ? (
                <OnboardingSolutions
                    isOpenedFromApp={isOpenedFromApp}
                    initialPrompt={querystringParams.get('draft') ?? undefined}
                    isDescribeOpen={step === 'describe'}
                    onViewChange={setSolutionsView}
                    isLoading={isLoading}
                    onDescribe={(text) => runSuggestion(text, true)}
                    onSolutionSelect={(tool) => void goToTool(tool, 'solutions_catalog')}
                />
            ) : (
                <Content $wide={step === 'suggestion'}>
                    {step === 'prompt' && (
                        <Stack $gap='22px'>
                            <Stack $gap='12px'>
                                <Title>{T._('What would you like to do with Zakeke?', 'Admin')}</Title>
                                <Subtitle>
                                    {T._(
                                        'Zakeke has several solutions, and they do very different things. Pick what sounds most like you and we will point you to the right one.',
                                        'Admin'
                                    )}
                                </Subtitle>
                            </Stack>

                            <OptionList>
                                {PROMPT_PRESETS.map((preset) => (
                                    <OptionRow key={preset.toolId} type='button' disabled={isLoading} onClick={() => onOptionClick(preset)}>
                                        <OptionText>{preset.label}</OptionText>
                                        <OptionArrow aria-hidden='true'>→</OptionArrow>
                                    </OptionRow>
                                ))}

                                <FreeText $isOpen={isFreeTextOpen}>
                                    {isFreeTextOpen ? (
                                        <FreeTextPanel onSubmit={onFreeTextSubmit}>
                                            <FreeTextLabel htmlFor='onboarding-free-text'>{T._('Tell us in your own words', 'Admin')}</FreeTextLabel>
                                            <PromptArea
                                                id='onboarding-free-text'
                                                ref={promptRef}
                                                rows={3}
                                                maxLength={2000}
                                                disabled={isLoading}
                                                value={freeText}
                                                placeholder={PROMPT_PLACEHOLDER}
                                                onChange={(event) => {
                                                    setFreeText(event.target.value);
                                                    setIsFreeTextEmptyError(false);
                                                }}
                                            />
                                            {isFreeTextEmptyError && (
                                                <FreeTextError>{T._('Write a line first, then we can point you somewhere.', 'Admin')}</FreeTextError>
                                            )}
                                            <FreeTextActions>
                                                <PrimaryButton type='submit' disabled={isLoading}>
                                                    {T._('Find my solution', 'Admin')}
                                                </PrimaryButton>
                                                <BasicButton type='button' disabled={isLoading} onClick={onFreeTextCancel}>
                                                    {T._('Cancel', 'Admin')}
                                                </BasicButton>
                                            </FreeTextActions>
                                        </FreeTextPanel>
                                    ) : (
                                        <FreeTextToggle type='button' disabled={isLoading} onClick={onFreeTextOpen}>
                                            {T._('None of these? Tell us in your own words', 'Admin')}
                                        </FreeTextToggle>
                                    )}
                                </FreeText>
                            </OptionList>

                            <ExploreFoot>
                                <ExploreLink type='button' disabled={isLoading} onClick={goToSolutions}>
                                    {T._("I'd rather explore on my own", 'Admin')}
                                </ExploreLink>
                            </ExploreFoot>

                            <StatusNote aria-live='polite'>{isLoading ? T._('Reading what you wrote…', 'Admin') : ''}</StatusNote>
                        </Stack>
                    )}

                    {step === 'suggestion' && suggestion && suggestedTool && (
                        <Stack $gap='16px'>
                            <GoalCard>
                                <GoalContent>
                                    <GoalLabel>{T._('Your goal', 'Admin')}</GoalLabel>
                                    <GoalText>{suggestion.isCustom ? `“${suggestion.prompt}”` : suggestion.prompt}</GoalText>
                                </GoalContent>
                                <GoalChange type='button' onClick={changeGoal}>
                                    {T._('Change', 'Admin')}
                                </GoalChange>
                            </GoalCard>

                            <ToolCard>
                                <ToolDetails>
                                    <CardLabel>{T._('Your solution', 'Admin')}</CardLabel>
                                    <ToolName>
                                        <suggestedTool.logo aria-hidden='true' />
                                        {suggestedTool.name}
                                    </ToolName>
                                    <ToolGoal>{suggestedTool.goal}</ToolGoal>
                                    <FeatureList>
                                        {suggestedTool.features.map((feature) => (
                                            <li key={feature}>{renderFeature(feature)}</li>
                                        ))}
                                    </FeatureList>
                                    <ToolCta type='button' $size='large' onClick={() => goToTool(suggestedTool, 'solution_result')}>
                                        {suggestedTool.ctaLabel}
                                    </ToolCta>
                                    <DemoNote>{T._('Your account will start with a few demo products you can try or delete.', 'Admin')}</DemoNote>
                                    <AlternativesNote>
                                        {T._('Not what you had in mind?', 'Admin')}{' '}
                                        <TextLink type='button' onClick={goToSolutions}>
                                            {T._('See all Zakeke solutions', 'Admin')}
                                        </TextLink>
                                    </AlternativesNote>
                                </ToolDetails>
                                <ToolPreview>
                                    <ToolPreviewVideo src={suggestedTool.video} autoPlay loop muted playsInline aria-hidden='true' />
                                </ToolPreview>
                            </ToolCard>
                        </Stack>
                    )}
                </Content>
            )}

            {step === 'prompt' && !isOpenedFromApp && (
                <ShortcutFooter>
                    <span>{T._('Or go straight to:', 'Admin')}</span>
                    {ONBOARDING_TOOLS.map((tool) => (
                        <Shortcut key={tool.id} type='button' onClick={() => goToTool(tool, 'shortcut')}>
                            {tool.name}
                        </Shortcut>
                    ))}
                </ShortcutFooter>
            )}
        </Container>
    );
};

export default Onboarding;
